import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut, Mail, Phone, ArrowLeft, Plus, Trash2, Package, ShoppingBag, MessagesSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "em_admin_token";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[#8A91A5]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const ProductForm = ({ token, onCreated }) => {
  const [form, setForm] = useState({
    title: "", description: "", price_eur: "9.90", path: "women", kind: "pdf",
    cover_url: "", duration_min: "", active: true, sort_order: "0",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) fd.append(k, v);
      });
      if (file) fd.append("file", file);
      await axios.post(`${API}/admin/products`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success("Produkt angelegt.");
      setForm({ ...form, title: "", description: "", price_eur: "9.90", cover_url: "", duration_min: "" });
      setFile(null);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#2C313D] focus:border-[#D4AF37] outline-none py-2 text-white text-sm";

  return (
    <form data-testid="admin-product-form" onSubmit={submit} className="border border-[#2C313D] bg-[#1A1D24] p-6 space-y-5">
      <h3 className="text-sm uppercase tracking-[0.22em] text-[#D4AF37] flex items-center gap-2"><Plus size={16} /> Neues Produkt</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Titel"><input data-testid="prod-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></Field>
        <Field label="Pfad">
          <select data-testid="prod-path" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} className={inputCls + " bg-[#1A1D24]"}>
            <option value="women">Frauen</option>
            <option value="men">Männer</option>
          </select>
        </Field>
        <Field label="Typ">
          <select data-testid="prod-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls + " bg-[#1A1D24]"}>
            <option value="pdf">PDF</option>
            <option value="audio">Audio</option>
            <option value="video">Video</option>
          </select>
        </Field>
        <Field label="Preis (€)"><input data-testid="prod-price" required type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className={inputCls} /></Field>
        <Field label="Dauer (min, optional)"><input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} className={inputCls} /></Field>
        <Field label="Cover-Bild URL (optional)"><input value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className={inputCls} /></Field>
        <Field label="Reihenfolge"><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputCls} /></Field>
        <Field label="Aktiv">
          <select value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })} className={inputCls + " bg-[#1A1D24]"}>
            <option value="1">Ja</option>
            <option value="0">Nein</option>
          </select>
        </Field>
      </div>
      <Field label="Beschreibung"><textarea data-testid="prod-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls + " resize-none"} /></Field>
      <Field label="Datei (PDF/MP3/MP4)"><input data-testid="prod-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-white text-sm" /></Field>
      <button data-testid="prod-submit" type="submit" disabled={busy} className="btn-gold">
        {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Anlegen
      </button>
    </form>
  );
};

const ProductsTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: { "X-Admin-Token": token } });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const del = async (id) => {
    if (!window.confirm("Produkt wirklich löschen?")) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers: { "X-Admin-Token": token } });
    toast.success("Gelöscht.");
    reload();
  };

  return (
    <div className="space-y-8">
      <ProductForm token={token} onCreated={reload} />
      {loading ? <p className="text-[#8A91A5]">Lade…</p> : items.length === 0 ? (
        <p data-testid="admin-products-empty" className="text-[#8A91A5]">Noch keine Produkte angelegt.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-product-${p.id}`} className="border border-[#2C313D] bg-[#1A1D24] p-5">
              <div className="flex items-center justify-between">
                <span className={`text-[0.65rem] uppercase tracking-[0.22em] px-2 py-0.5 ${p.path === "men" ? "text-[#D4AF37] border border-[#D4AF37]" : "text-[#9EB29F] border border-[#9EB29F]"}`}>{p.path} · {p.kind}</span>
                <button data-testid={`admin-prod-del-${p.id}`} onClick={() => del(p.id)} className="text-[#8A91A5] hover:text-red-400"><Trash2 size={14} /></button>
              </div>
              <h4 className="mt-3 text-lg text-white">{p.title}</h4>
              <p className="mt-1 text-xs text-[#8A91A5] line-clamp-2">{p.description}</p>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-[#D4AF37]">{p.price_eur.toFixed(2).replace(".", ",")} €</span>
                <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[#8A91A5]">{p.active ? "aktiv" : "inaktiv"}</span>
              </div>
              {p.file_name ? <p className="mt-2 text-[0.65rem] text-[#5b6273]">Datei: {p.file_name}</p> : <p className="mt-2 text-[0.65rem] text-[#B05D3E]">Keine Datei hochgeladen</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OrdersTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Token": token } });
        setItems(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (loading) return <p className="text-[#8A91A5]">Lade…</p>;
  if (items.length === 0) return <p data-testid="admin-orders-empty" className="text-[#8A91A5]">Noch keine Bestellungen.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-[#2C313D] bg-[#1A1D24] p-5">
          <div className="flex items-center justify-between">
            <h4 className="text-lg text-white">{o.product_title}</h4>
            <span className={`text-[0.65rem] uppercase tracking-[0.22em] px-2 py-0.5 ${o.payment_status === "paid" ? "text-[#9EB29F] border border-[#9EB29F]" : "text-[#B05D3E] border border-[#B05D3E]"}`}>{o.payment_status}</span>
          </div>
          <p className="mt-2 text-sm text-[#8A91A5]">{o.customer_name} · {o.customer_email}</p>
          <p className="mt-1 text-sm text-[#D4AF37]">{o.amount_eur.toFixed(2).replace(".", ",")} {o.currency?.toUpperCase()}</p>
          <p className="mt-2 text-[0.65rem] uppercase tracking-[0.22em] text-[#5b6273]">{new Date(o.created_at).toLocaleString()} · DL × {o.download_count}</p>
        </div>
      ))}
    </div>
  );
};

const ContactsTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/contacts`, { headers: { "X-Admin-Token": token } });
        setItems(res.data);
      } finally { setLoading(false); }
    })();
  }, []);
  const filtered = items.filter((c) => filter === "all" ? true : c.path === filter);
  if (loading) return <p className="text-[#8A91A5]">Lade…</p>;
  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        {[["all", "Alle"], ["women", "Frauen"], ["men", "Männer"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-filter-${k}`} onClick={() => setFilter(k)}
                  className={`px-4 py-2 text-xs uppercase tracking-[0.22em] border transition ${filter === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#2C313D] text-[#8A91A5] hover:text-white"}`}>
            {l} ({k === "all" ? items.length : items.filter(c => c.path === k).length})
          </button>
        ))}
      </div>
      {filtered.length === 0 ? <p data-testid="admin-empty" className="text-[#8A91A5]">Noch keine Anfragen.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`admin-contact-${c.id}`} className="border border-[#2C313D] bg-[#1A1D24] p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg text-white">{c.name}</h3>
                <span className={`text-[0.65rem] uppercase tracking-[0.22em] px-2 py-1 ${c.path === "men" ? "text-[#D4AF37] border border-[#D4AF37]" : "text-[#9EB29F] border border-[#9EB29F]"}`}>{c.path}</span>
              </div>
              <div className="mt-3 space-y-1 text-sm text-[#8A91A5]">
                <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-white"><Mail size={14} /> {c.email}</a>
                {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:text-white"><Phone size={14} /> {c.phone}</a>}
              </div>
              <p className="mt-4 text-white/90 text-sm leading-relaxed whitespace-pre-line">{c.message}</p>
              <p className="mt-4 text-[0.7rem] uppercase tracking-[0.22em] text-[#5b6273]">{new Date(c.created_at).toLocaleString()} · {c.language?.toUpperCase()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("contacts");

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "X-Admin-Token": input } });
      localStorage.setItem(TOKEN_KEY, input);
      setToken(input); setInput(""); setError("");
    } catch { setError("Falsches Passwort."); }
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); };

  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center p-6">
        <form onSubmit={handleLogin} className="w-full max-w-sm space-y-6">
          <div className="flex items-center gap-3 text-[#D4AF37]">
            <Lock size={20} /><span className="uppercase tracking-[0.24em] text-sm">Admin</span>
          </div>
          <input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin Token" required
                 className="w-full bg-transparent border-b border-[#2C313D] focus:border-[#D4AF37] outline-none py-3 text-white" />
          {error && <p className="text-red-400 text-sm" data-testid="admin-error">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" className="btn-gold w-full justify-center">Login</button>
          <Link to="/" className="block text-center text-xs uppercase tracking-[0.24em] text-[#8A91A5] hover:text-white">
            <ArrowLeft className="inline mr-2" size={12} /> Zurück
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F1014] text-white">
      <header className="border-b border-[#2C313D] px-6 md:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="entry-display text-lg">Enrico Müller</span>
          <span className="uppercase tracking-[0.24em] text-xs text-[#D4AF37]">Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-xs uppercase tracking-[0.24em] text-[#8A91A5] hover:text-white">Site</Link>
          <button data-testid="admin-logout" onClick={logout} className="text-xs uppercase tracking-[0.24em] inline-flex items-center gap-2 text-[#8A91A5] hover:text-white">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        <div className="flex items-center gap-2 mb-8 border-b border-[#2C313D]">
          {[
            ["contacts", "Anfragen", MessagesSquare],
            ["products", "Produkte", Package],
            ["orders", "Bestellungen", ShoppingBag],
          ].map(([k, l, Icon]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                    className={`px-4 py-3 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-2 transition border-b-2 ${tab === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#8A91A5] hover:text-white"}`}>
              <Icon size={14} /> {l}
            </button>
          ))}
        </div>

        {tab === "contacts" && <ContactsTab token={token} />}
        {tab === "products" && <ProductsTab token={token} />}
        {tab === "orders" && <OrdersTab token={token} />}
      </div>
    </div>
  );
};

export default AdminPage;
